import React from "react";
import { Modal, Row, Col, Form } from "react-bootstrap";
import Select from "react-select";

import ConditionalButton from "../../../../../layout/conditional-button";
import ConfirmModal from "../../../../../layout/modal";
import { isGuid } from "../../../../../utils/string";

const ACTIVITY_OPTIONS = [
    { value: "NOTE", label: "Note" },
    { value: "CALL", label: "Call" },
    { value: "EMAIL", label: "Email" },
    { value: "SMS", label: "SMS" },
    { value: "MEETING", label: "Meeting" },
    { value: "TASK", label: "Task" },
    { value: "FOLLOW_UP", label: "Follow Up" },
];

const activityLabel = (type) => ACTIVITY_OPTIONS.find((item) => item.value === type)?.label || type || "Activity";
const FOLLOW_UP_TYPES = ["TASK", "FOLLOW_UP"];

const toDateTimeLocal = (value) => {
    if (!value) return "";
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const offset = date.getTimezoneOffset();
    const local = new Date(date.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
};

const fromDateTimeLocal = (value) => value ? new Date(value).toISOString() : null;

class LeadActivityModal extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            ...this.mapPropsToState(props),
            staff: [],
            pendingSave: null,
            updateFutureFollowUps: {
                show: false
            }
        };
    }

    mapPropsToState = (props) => ({
        uuid: props.uuid || null,
        leadUuid: props.leadUuid,
        type: props.type || "NOTE",
        subject: props.subject || "",
        body: props.body || "",
        dueAt: toDateTimeLocal(props.dueAt),
        completedAt: props.completedAt || null,
        assignedToUuid: props.assignedToUuid || "",
        originalAssignedToUuid: props.assignedToUuid || "",
    });

    componentDidUpdate(oldProps) {
        if (oldProps.show !== this.props.show || oldProps.leadUuid !== this.props.leadUuid || oldProps.uuid !== this.props.uuid) {
            this.setState(prev => ({
                ...prev,
                ...this.mapPropsToState(this.props),
                pendingSave: null,
                updateFutureFollowUps: {
                    show: false
                }
            }));
            if (this.props.show && !oldProps.show) this.loadStaff();
        }
    }

    componentDidMount() {
        if (this.props.show) this.loadStaff();
    }

    loadStaff = async () => {
        if (!this.props.getStaff) return;
        const result = await this.props.getStaff({});
        const staff = (Array.isArray(result) ? result : []).map((item) => ({
            ...item,
            value: item.uuid,
            label: item.Person?.displayName || [item.Person?.firstName, item.Person?.lastName].filter(Boolean).join(" ") || item.number || item.uuid,
        }));
        this.setState({ staff });
    };

    onHideModal = (e) => {
        if (this.props.onCancel) this.props.onCancel(e);
    };

    onChange = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value });
    };

    onTypeChange = (option) => {
        this.setState({ type: option?.value || "NOTE" });
    };

    onAssignedToChange = (option) => {
        this.setState({ assignedToUuid: option?.value || "" });
    };

    buildPayload = () => {
        const subject = String(this.state.subject || "").trim();
        const body = String(this.state.body || "").trim();

        return {
            uuid: this.state.uuid,
            leadUuid: this.state.leadUuid,
            type: this.state.type || "NOTE",
            subject: subject || activityLabel(this.state.type),
            body,
            dueAt: fromDateTimeLocal(this.state.dueAt),
            completedAt: this.state.completedAt,
            assignedToUuid: this.state.assignedToUuid || null,
        };
    };

    onOk = async () => {
        if (!this.canSave) return;

        const payload = this.buildPayload();
        if (this.shouldPromptFutureFollowUps(payload)) {
            this.setState({
                pendingSave: payload,
                updateFutureFollowUps: {
                    show: true,
                    title: "Update future follow-ups?",
                    body: "The assigned person changed for this scheduled lead follow-up. Update all future open lead follow-ups for this person to match this assignment?"
                }
            });
            return;
        }

        await this.save(payload, false);
    };

    onFutureFollowUpsCancel = () => {
        this.setState({
            pendingSave: null,
            updateFutureFollowUps: {
                show: false
            }
        });
    };

    onFutureFollowUpsSaveOne = async () => {
        await this.save(this.state.pendingSave, false);
    };

    onFutureFollowUpsSaveAll = async () => {
        await this.save(this.state.pendingSave, true);
    };

    save = async (payload, updateFutureFollowUps) => {
        await this.props.saveLeadActivity({
            ...payload,
            updateFutureFollowUps
        });

        if (this.props.onOk) this.props.onOk(this.state);
    };

    shouldPromptFutureFollowUps = (payload) => {
        if (!isGuid(payload?.uuid)) return false;
        if (!FOLLOW_UP_TYPES.includes(payload.type)) return false;
        if (payload.completedAt) return false;
        if (!payload.dueAt) return false;

        const dueAt = new Date(payload.dueAt);
        if (Number.isNaN(dueAt.getTime()) || dueAt < new Date()) return false;

        return (payload.assignedToUuid || "") !== (this.state.originalAssignedToUuid || "");
    };

    get canSave() {
        return isGuid(this.state.leadUuid) && Boolean(String(this.state.subject || this.state.body || "").trim());
    }

    render() {
        const staffOptions = this.state.staff || [];

        return (
            <>
                <Modal size="lg" backdrop="static" keyboard show={this.props.show} onHide={this.onHideModal}>
                    <Modal.Header closeButton>
                        <Modal.Title id="staticBackdropLabel">Lead Activity</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row className="g-3 pb-3 mt-1">
                            <Col md="4">
                                <div className="top-label">
                                    <Select
                                        inputId="leadActivityType"
                                        classNamePrefix="react-select"
                                        options={ACTIVITY_OPTIONS}
                                        value={ACTIVITY_OPTIONS.find((option) => option.value === this.state.type) || ACTIVITY_OPTIONS[0]}
                                        onChange={this.onTypeChange}
                                        isClearable={false}
                                    />
                                    <Form.Label>TYPE</Form.Label>
                                </div>
                            </Col>
                            <Col md="8">
                                <div className="top-label">
                                    <Form.Control
                                        type="text"
                                        name="subject"
                                        value={this.state.subject}
                                        onChange={this.onChange}
                                    />
                                    <Form.Label>SUBJECT</Form.Label>
                                </div>
                            </Col>
                            <Col md="6">
                                <div className="top-label">
                                    <Form.Control
                                        type="datetime-local"
                                        name="dueAt"
                                        value={this.state.dueAt}
                                        onChange={this.onChange}
                                    />
                                    <Form.Label>DUE</Form.Label>
                                </div>
                            </Col>
                            <Col md="6">
                                <div className="top-label">
                                    <Select
                                        inputId="leadActivityAssignedTo"
                                        classNamePrefix="react-select"
                                        options={staffOptions}
                                        value={staffOptions.find((option) => option.value === this.state.assignedToUuid) || null}
                                        onChange={this.onAssignedToChange}
                                        isClearable
                                    />
                                    <Form.Label>ASSIGNED TO</Form.Label>
                                </div>
                            </Col>
                            <Col xs="12">
                                <div className="top-label">
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        name="body"
                                        value={this.state.body}
                                        onChange={this.onChange}
                                    />
                                    <Form.Label>DETAIL</Form.Label>
                                </div>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <ConditionalButton enabledVariant="outline-primary" display enabled onEnabledClick={this.onHideModal} icon="error-hexagon">cancel</ConditionalButton>
                        <ConditionalButton enabledVariant="outline-primary" display enabled={this.canSave} onEnabledClick={this.onOk} icon="save">ok</ConditionalButton>
                    </Modal.Footer>
                </Modal>
                <ConfirmModal
                    {...this.state.updateFutureFollowUps}
                    onCancel={this.onFutureFollowUpsCancel}
                    buttons={[
                        {
                            caption: "this activity only",
                            variant: "outline-primary",
                            display: true,
                            enabled: true,
                            onEnabledClick: this.onFutureFollowUpsSaveOne
                        },
                        {
                            caption: "update future follow-ups",
                            variant: "primary",
                            display: true,
                            enabled: true,
                            onEnabledClick: this.onFutureFollowUpsSaveAll
                        },
                        {
                            default: true,
                            caption: "cancel",
                            variant: "outline-primary",
                            display: true,
                            enabled: true,
                            onEnabledClick: this.onFutureFollowUpsCancel
                        }
                    ]}
                />
            </>
        );
    }
}

export default LeadActivityModal;
